import React from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import lodash, { isObject, } from 'lodash';
import moment from 'moment';
import Confirm from '../../confirm/Confirm';
import ObjectUtils from '../../../modules/ObjectUtils';
import VerticalLabelList from '../../label/VerticalLabelList';
import BraintreePaymentService from '../../../service/BraintreePaymentService';
import FieldSet from '../../fieldset/FieldSet';
import Grid from '../../grid/Grid';
import { EMPTY_GRID_DEFAULT_MESSAGE, } from '../../../modules/Constants';
import CustomerProfileService from '../../../service/CustomerService';
import PaymentGatewayService from '../../../service/PaymentGatewayService';

class DetailedCustomerProfile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      customer: null,
      selectedPaymentAccount: null,
      paymentAccountHeaders: [],
      paymentAccountRows: [],
      linkedCustomersHeaders: [],
      linkedCustomersRows: [],
    };

    this.load = () => {
      const { customer, } = this.props;
      let headers = [];
      let rows = [];

      PaymentGatewayService.paymentAccounts.getPaymentAccountsByCustomerId(customer.customer.id)
        .then((detailResponse) => {
          const { data, } = detailResponse;
          const { paymentAccount, } = data;
          if (paymentAccount && paymentAccount.length > 0) {
            headers = Object.keys(paymentAccount[0]).map(header => ({
              value: header,
              display: lodash.startCase(header),
            }));
            paymentAccount.forEach((account) => {
              rows.push({
                value: Object.values(account),
                raw: account,
              });
            });
          }
          rows = ObjectUtils.clearNullValues(rows);

          this.setState(() => ({
            customer: ObjectUtils.clearNullValues(customer),
            paymentAccountHeaders: headers,
            paymentAccountRows: rows,
          }));
        })
        .catch((err) => {
          console.error(err);
        });

      const linkedCustomersRows = [];
      const linkedCustomersHeaders = [
        {
          value: 'customerId',
          display: 'Customer Id',
        },
        {
          value: 'linkedCustomerIds',
          display: 'Linked Customer Ids',
        },
      ];

      CustomerProfileService.getLinkedCustomers(customer.customer.id)
        .then((response) => {
          const { links, } = response.data;
          if (response.status === 200) {
            linkedCustomersRows.push({
              value: [
                links.customerId,
                links.linkedCustomerIds,
              ],
              raw: links,
            });
          }
          this.setState({
            linkedCustomersHeaders,
            linkedCustomersRows,
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
        });
    };
    this.displayKeyExclusions = [ 'paymentAccounts', 'verificationCode', 'hasVerifiedEmailAddress', ];
  }

  componentDidMount() {
    this.load();
  }

  onRowSelect(selectedRow) {
    const { paymentAccountRows, } = this.state;
    const selectedPaymentAccount = paymentAccountRows.find((paymentAccount) => {
      const { raw, } = paymentAccount;
      return selectedRow.id === raw.id;
    });

    this.setState({
      selectedPaymentAccount: selectedPaymentAccount.raw,
    });
  }

  handleHide = () => {
    this.setState({ status: '', });
  };

  deletePaymentAccount(customerId, accountId) {
    BraintreePaymentService.deletePaymentAccount(customerId, accountId)
      .then(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="success">
              <Alert.Heading>Success!</Alert.Heading>
              <p>{`Account ${accountId} deleted successfuly!`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-success">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      })
      .catch(() => {
        this.setState({
          status: (
            <Alert className="mt-2" variant="danger">
              <Alert.Heading>Error!</Alert.Heading>
              <p>{`Unable to delete account ${accountId}`}</p>
              <div className="d-flex justify-content-end">
                <Button onClick={this.handleHide} variant="outline-danger">
                  OK
                </Button>
              </div>
            </Alert>
          ),
        });
      });
  }

  showSelectedPaymentAccount(paymentAccount) {
    const {
      customer, isLoading, status, showDeleteConfirmation = false,
    } = this.state;
    if (!paymentAccount) {
      return null;
    }
    const deletePaymentMethod = () => {
      this.deletePaymentAccount(customer.customer.id, paymentAccount.id);
      this.setState({
        showDeleteConfirmation: false,
      });
    };
    const confirmEvent = () => {
      this.setState({
        showDeleteConfirmation: true,
      });
    };
    const onNo = () => {
      this.setState({
        showDeleteConfirmation: false,
      });
    };
    return (
      <div>
        <p>{`Selected ID: ${paymentAccount.id}`}</p>
        <button type="button" className="btn btn-danger" onClick={() => confirmEvent()}>
          Delete
        </button>
        <Confirm
          open={showDeleteConfirmation}
          title="Delete Payment Method"
          content="Are you sure you want to continue?"
          onYes={() => deletePaymentMethod()}
          onClose={onNo}
          onNo={onNo}
          isLoading={isLoading}
        />
        {status}
      </div>
    );
  }

  render() {
    const {
      customer,
      paymentAccountHeaders = [],
      paymentAccountRows = [],
      selectedPaymentAccount,
      linkedCustomersHeaders = [],
      linkedCustomersRows = [],
    } = this.state;
    if (!customer) {
      return null;
    }

    // format date fields
    customer.customer.deletionTime = formatDate(customer.customer.deletionTime);
    if (isObject(customer.account)) {
      customer.account.creationEpoch = formatDate(customer.account.creationEpoch);
      customer.account.passwordLastUpdated = formatDate(customer.account.passwordLastUpdated);
      customer.account.emailVerifiedTimestamp = formatDate(customer.account.emailVerifiedTimestamp);
    }
    if (isObject(customer.customerVerificationCode)) {
      customer.customerVerificationCode.createdAt = formatDate(customer.customerVerificationCode.createdAt);
    }
     
    if (isObject(paymentAccountRows)) {
      paymentAccountRows.forEach((account) => {
        account.value[2] = formatDate(account.value[2]);
        account.value[3] = formatDate(account.value[3]);
      });
    }
     

    const customerItems = VerticalLabelList.toList(customer.customer, {
      displayFormatter: (value) => {
        const valueCheck = (typeof value !== 'undefined' && value) ? value : '';
        const formattedValue = valueCheck.split('_').join(' ');
        return lodash.startCase(formattedValue);
      },
    });
    const accountItems = VerticalLabelList.toList(customer.account, {
      displayFormatter: (value) => {
        const valueCheck = (typeof value !== 'undefined' && value) ? value : '';
        let formattedValue = valueCheck.split('_').join(' ');
        // override to set principle id to customer id
        if (formattedValue === 'principleID') {
          formattedValue = 'customerID';
        }
        return lodash.startCase(formattedValue);
      },
    });
    const verificationCodeitems = VerticalLabelList.toList(customer.customerVerificationCode, {
      displayFormatter: (value) => {
        const valueCheck = (typeof value !== 'undefined' && value) ? value : '';
        const formattedValue = valueCheck.split('_').join(' ');
        return lodash.startCase(formattedValue);
      },
    });

    return (
      <div>
        <FieldSet title="Customer Summary" style={{ maxHeight: '400px', }}>
          <VerticalLabelList items={customerItems} exclusions={this.displayKeyExclusions} />
        </FieldSet>
        <div style={{ marginTop: '50px', }}>
          <FieldSet title="Account Summary" style={{ maxHeight: '400px', }}>
            <VerticalLabelList items={accountItems} exclusions={this.displayKeyExclusions} />
          </FieldSet>
        </div>
        <div style={{ marginTop: '50px', }}>
          <FieldSet title="Customer Verification Code Summary" style={{ maxHeight: '400px', }}>
            <VerticalLabelList items={verificationCodeitems} exclusions={this.displayKeyExclusions} />
          </FieldSet>
        </div>
        <div style={{ marginTop: '50px', }}>
          <FieldSet title="Payment Accounts" style={{ maxHeight: '400px', }}>
            <Grid
              headers={paymentAccountHeaders}
              rows={paymentAccountRows}
              onEmptyGridMessage={EMPTY_GRID_DEFAULT_MESSAGE}
              onRowSelect={row => this.onRowSelect(row)}
            />
          </FieldSet>
        </div>
        {this.showSelectedPaymentAccount(selectedPaymentAccount)}
        <div style={{ marginTop: '50px', }}>
          <FieldSet title="Linked Customers" style={{ maxHeight: '400px', }}>
            <Grid
              headers={linkedCustomersHeaders}
              rows={linkedCustomersRows}
              onEmptyGridMessage={EMPTY_GRID_DEFAULT_MESSAGE}
            />
          </FieldSet>
        </div>
      </div>
    );
  }
}

function formatDate(epochMillis) {
  return epochMillis ? moment.unix(epochMillis).utc().format() : '';
}

DetailedCustomerProfile.propTypes = {
  customer: PropTypes.object.isRequired,
};

export default DetailedCustomerProfile;
